import type { ThemedImageAssetPath } from "@/types/assets"
import type { ArduinoDevice, ArduinoDeviceFamily } from "@/types/devices"

export const ManualDeviceFqbn = "device:generic:manual"

/** Deep link that opens the Arduino App Lab desktop application on the Arduino Cloud screen. */
export const APP_LAB_DEEP_LINK = "arduino-app-lab://arduinocloud"
/** Public download page for the Arduino App Lab application. */
export const APP_LAB_DOWNLOAD_URL = "https://www.arduino.cc/en/software/#app-lab-section"

export const STATUS_OPTIONS = [
  { value: "ONLINE", label: "Online" },
  { value: "OFFLINE", label: "Offline" }
] as const satisfies readonly { value: ArduinoDevice["device_status"]; label: string }[]

export const boardFQBN = {
  UNOQ: "arduino:zephyr:unoq",
  VENTUNOQ: "arduino:zephyr:ventunoq",
  OPTA: "arduino:mbed_opta:opta",
  NANO33IOT: "arduino:samd:nano_33_iot",
  MKRWIFI1010: "arduino:samd:mkrwifi1010",
  UNOR4: "arduino:renesas_uno:unor4wifi",
  MKRNB1500: "arduino:samd:mkrnb1500",
  MKRWAN1300: "arduino:samd:mkrwan1300",
  MKRWAN1310: "arduino:samd:mkrwan1310",
  MKR1000: "arduino:samd:mkr1000",
  MKRGSM1400: "arduino:samd:mkrgsm1400",
  PORTENTAH7: "arduino:mbed_portenta:envie_m7",
  NANORP2040CONNECT: "arduino:mbed_nano:nanorp2040connect",
  NICLAVISION: "arduino:mbed_nicla:nicla_vision",
  GIGA: "arduino:mbed_giga:giga",
  PORTENTAC33: "arduino:renesas_portenta:portenta_c33",
  NANOESP32: "arduino:esp32:nano_nora"
} as const

/**
 * FQBNs of boards that must be provisioned from Arduino App Lab instead of the web (Q family).
 */
export const APP_LAB_FQBNS: readonly string[] = [boardFQBN.UNOQ, boardFQBN.VENTUNOQ]

export const arduinoDeviceDataMap = {
  [boardFQBN.NANO33IOT]: {
    docs: "hardware/nano-33-iot",
    family: "nano"
  },
  "arduino:samd:mkrzero": {
    docs: "hardware/mkr-zero",
    family: "mkr"
  },
  [boardFQBN.MKRWIFI1010]: {
    docs: "hardware/mkr-wifi-1010",
    family: "mkr"
  },
  [boardFQBN.MKRWAN1310]: {
    docs: "hardware/mkr-wan-1310",
    family: "mkr"
  },
  [boardFQBN.MKRWAN1300]: {
    docs: "hardware/mkr-wan-1300",
    family: "mkr"
  },
  "arduino:samd:mkrvidor4000": {
    docs: "hardware/mkr-vidor-4000",
    family: "mkr"
  },
  [boardFQBN.MKRNB1500]: {
    docs: "hardware/mkr-nb-1500",
    family: "mkr"
  },
  [boardFQBN.MKRGSM1400]: {
    docs: "hardware/mkr-gsm-1400",
    family: "mkr"
  },
  "arduino:samd:mkrfox1200": {
    docs: "hardware/mkr-fox-1200",
    family: "mkr"
  },
  [boardFQBN.MKR1000]: {
    docs: "hardware/mkr-1000-wifi",
    family: "mkr"
  },
  "arduino:samd:arduino_zero_native": {
    docs: "hardware/zero",
    family: "uno"
  },
  "arduino:samd:arduino_zero_edbg": {
    docs: "hardware/zero",
    family: "uno"
  },
  "arduino:sam:arduino_due_x_dbg": {
    docs: "hardware/due",
    family: "mega"
  },
  "arduino:sam:arduino_due_x": {
    docs: "hardware/due",
    family: "mega"
  },
  "arduino:megaavr:uno2018": {
    docs: "hardware/uno-wifi-rev2",
    family: "uno"
  },
  "arduino:megaavr:nona4809": {
    docs: "hardware/nano-every",
    family: "nano"
  },
  /*   "arduino:mbed_rp2040:pico": {}, */
  "arduino:mbed_portenta:portenta_x8": {
    docs: "hardware/portenta-x8",
    family: "portenta"
  },
  "arduino:python:portenta_x8": {
    docs: "hardware/portenta-x8",
    family: "portenta"
  },
  [boardFQBN.PORTENTAH7]: {
    docs: "hardware/portenta-h7",
    family: "portenta"
  },
  "arduino:mbed_niclabe:nicla_voice": {
    docs: "hardware/nicla-voice",
    family: "nicla"
  },
  [boardFQBN.NICLAVISION]: {
    docs: "hardware/nicla-vision",
    family: "nicla"
  },
  "arduino:mbed_nicla:nicla_sense": {
    docs: "hardware/nicla-sense-me",
    family: "nicla"
  },
  [boardFQBN.NANORP2040CONNECT]: {
    docs: "hardware/nano-rp2040-connect",
    family: "nano"
  },
  "arduino:mbed_nano:nano33ble": {
    docs: "hardware/nano-33-ble",
    family: "nano"
  },
  [boardFQBN.OPTA]: {
    docs: "hardware/opta",
    family: "opta"
  },
  "arduino:mbed_edge:edge_control": {
    docs: "hardware/edge-control",
    family: "nano"
  },
  "arduino:avr:yunmini": {
    docs: "retired/boards/arduino-yun-mini",
    family: "unknown"
  },
  "arduino:avr:yun": {
    docs: "retired/boards/arduino-yun",
    family: "unknown"
  },
  "arduino:avr:unowifi": {
    docs: "retired/boards/arduino-uno-wifi",
    family: "uno"
  },
  "arduino:avr:unomini": {
    docs: "hardware/uno-mini-le",
    family: "uno"
  },
  "arduino:avr:uno": {
    docs: "hardware/uno-rev3",
    family: "uno"
  },
  "arduino:avr:nano": {
    docs: "hardware/nano",
    family: "nano"
  },
  "arduino:avr:micro": {
    docs: "hardware/micro",
    family: "uno"
  },
  "arduino:avr:mega": {
    docs: "hardware/mega-2560",
    family: "mega"
  },
  "arduino:avr:leonardo": {
    docs: "hardware/leonardo",
    family: "uno"
  },
  "arduino:avr:ethernet": {
    docs: "hardware/ethernet-shield-rev2",
    family: "unknown"
  },
  "arduino:mbed_nano:nano33blesense": {
    docs: "hardware/nano-33-ble-sense",
    family: "nano"
  },
  [boardFQBN.GIGA]: {
    docs: "hardware/giga-r1-wifi",
    family: "mega"
  },
  [boardFQBN.PORTENTAC33]: {
    docs: "hardware/portenta-c33",
    family: "portenta"
  },
  [boardFQBN.UNOR4]: {
    docs: "hardware/uno-r4-wifi",
    family: "uno"
  },
  "arduino:renesas_uno:minima": {
    docs: "hardware/uno-r4-minima",
    family: "uno"
  },
  [boardFQBN.UNOQ]: {
    docs: "hardware/uno-q",
    family: "uno"
  },
  [boardFQBN.VENTUNOQ]: {
    docs: "hardware/ventuno-q",
    family: "uno"
  },
  [boardFQBN.NANOESP32]: {
    docs: "hardware/nano-esp32",
    family: "nano"
  },
  "esp32:esp32:nano_nora": {
    docs: "hardware/nano-esp32",
    family: "nano"
  }
} as const satisfies Record<
  string,
  {
    family: ArduinoDeviceFamily
    docs?: string
  }
>

export const deviceImgMap = {
  mega: "images/devices/boards/device-mega.svg",
  mkr: "images/devices/boards/device-mkr.svg",
  nano: "images/devices/boards/device-nano.svg",
  opta: "images/devices/boards/device-opta.svg",
  portenta: "images/devices/boards/device-portenta.svg",
  uno: "images/devices/boards/device-uno.svg",
  nicla: "images/devices/boards/device-uno.svg",
  unknown: "images/devices/boards/device-generic.svg"
} as const satisfies Partial<Record<ArduinoDeviceFamily, ThemedImageAssetPath>>

export const uniqueDeviceImgMap = {
  [boardFQBN.UNOQ]: "images/devices/boards/device-unoq.svg",
  [boardFQBN.VENTUNOQ]: "images/devices/boards/device-ventunoq.svg"
} as const satisfies Partial<Record<string, ThemedImageAssetPath>>

/*
 * ====================
 * === Device Constants ===
 * ====================
 */

export const ConnectionTypesNames = {
  wifi: "WiFi",
  eth: "Ethernet",
  wifiandsecret: "WiFi (& secret)",
  gsm: "GSM",
  nb: "NB",
  lora: "LoraWAN",
  catm1: "CAT-M1",
  cellular: "Cellular"
} as const satisfies Record<NonNullable<ArduinoDevice["connection_type"]>, string>
